import * as THREE from 'three';
import { createCarousel } from '../spikes/threejs/carousel';
import { createCoaster } from '../spikes/threejs/coaster';
import { orientGlowLayers } from '../spikes/threejs/glow';
import { createChannelMaterials, type ChannelMaterials, type ParkMaterials } from '../spikes/threejs/materials';
import {
  BENCH_PLACEMENT_YAW,
  createBenches,
  createHedges,
  createLamps,
  createPicketFence,
  createShrubs,
  createTrees,
  snapPlacedPropToGround,
} from '../spikes/threejs/props';
import { createRideQueue, queueLanePiece } from '../spikes/threejs/queue';
import { createFerrisWheel, createPirateShip, createSpinningRide } from '../spikes/threejs/rides';
import { createDrinkKiosk, createEntrance, createKiosk, createTicketBooth } from '../spikes/threejs/structures';
import { productionIslandSize, unlockedCanvasSubcellCenters } from '../spatial/buildableArea';
import {
  createExactCanvasLawn,
  createExactSubcellGrid,
  createIsland,
  createPathFloorPresentation,
  createPathNetwork,
  pathFloorCellLift,
  setSubgridCueMode,
  type SubgridCueMode,
} from '../spikes/threejs/terrain';
import {
  poseOnClosedPath,
  posePatrolWalker,
  walkedTime,
  type PatrolWalkerSpec,
  type PathPoint,
} from '../game/visitorLocomotion';
import {
  indexOfEquivalentVisitorRoute,
  nearestDistanceOnClosedPath,
  remapWalkerOffset,
  visitorRouteSetsEquivalent,
} from '../game/walkRoutes';
import { createVisitors, type Route } from '../spikes/threejs/visitors';
import { createDistantIslandPlotPreview, createOwnedIslandPlotPreview } from './landPreviewVisual';
import {
  CELL_SIZE,
  resolveSelectionAnchor,
  type EntityRenderSpec,
  type MappedVisual,
  type MigrationRenderModel,
  type WorldPoint,
} from './mapParkState';
import { createCarouselLevelMarker, createMigrationPlaceholder } from './placeholder';
import {
  applyQueuePoses,
  queueEvidenceFromLanes,
  queueGuestPoses,
  queueLanesFromModel,
  queueSignLabel,
  restSlotsForLane,
  rotatePointAroundY,
  kioskHasQueue,
  type QueueEvidence,
  type QueueLane,
} from './queuePresentation';

type ParkUpdater = (elapsed: number, delta: number) => void;

const CAROUSEL_SCALE = 0.58;
const COASTER_SCALE = 0.5;
const SPINNING_SCALE = 0.95;
const PIRATE_SCALE = 0.96;
/**
 * Yaw so the boat's swing plane is nearly parallel to the fixed 41° camera's screen plane:
 * the pendulum arc then reads as left-down-right with both ends rising, instead of one end
 * dropping down-screen because of the isometric projection.
 */
const PIRATE_YAW = THREE.MathUtils.degToRad(30);
const FERRIS_SCALE = 1.08;
const KIOSK_SCALE = 0.9;

/**
 * Legacy walker-corridor bound (former path slab `w: 1.65`, `d: CELL_SIZE + 0.2`).
 * Visible paving is the narrower 1.44 centered 3-subcell band.
 */
export const PATH_NETWORK_WIDTH = 1.65;

export const PRODUCTION_PATROL_WALKERS: readonly PatrolWalkerSpec[] = [
  { offset: 0.0, speed: 0.52, lane: -1, pauseEvery: 7.5, pauseFor: 0.65, pausePhase: 0.4 },
  { offset: 0.16, speed: 0.41, lane: 0, pauseEvery: 8.2, pauseFor: 0.45, pausePhase: 1.8 },
  { offset: 0.31, speed: -0.48, lane: 1, pauseEvery: 6.8, pauseFor: 0.7, pausePhase: 3.1 },
  { offset: 0.48, speed: 0.58, lane: -1, pauseEvery: 9.0, pauseFor: 0.5, pausePhase: 0.9 },
  { offset: 0.67, speed: -0.37, lane: 0, pauseEvery: 7.1, pauseFor: 0.8, pausePhase: 4.4 },
  { offset: 0.84, speed: 0.46, lane: 1, pauseEvery: 8.6, pauseFor: 0.55, pausePhase: 2.2 },
  { offset: 0.08, speed: 0.44, lane: 0, pauseEvery: 7.8, pauseFor: 0.5, pausePhase: 5.1 },
  { offset: 0.73, speed: -0.42, lane: -1, pauseEvery: 8.4, pauseFor: 0.6, pausePhase: 2.7 },
];

export function productionPatrolPoses(waypoints: WorldPoint[], elapsed: number) {
  const points: PathPoint[] = waypoints.map((point) => ({ x: point.x, z: point.z }));
  return PRODUCTION_PATROL_WALKERS.map((walker) => posePatrolWalker(points, walker, elapsed));
}

function v2(x: number, z: number): THREE.Vector2 {
  return new THREE.Vector2(x, z);
}

function islandSize(_model: MigrationRenderModel): { width: number; depth: number } {
  return productionIslandSize();
}

function tagVisual(object: THREE.Object3D, spec: EntityRenderSpec, visual: MappedVisual): void {
  object.userData.migration = {
    visual,
    kind: spec.kind,
    slotId: spec.slotId,
    entityId: spec.id,
    level: spec.level,
  };
}

const FERRIS_CHANNELS = createChannelMaterials({
  primary: 0x4f9ddb,
  secondary: 0xfff6e6,
  accent: 0xf2c14e,
  structure: 0xd08a4a,
  lights: 0xfff1b0,
});

const SPINNING_CHANNELS = createChannelMaterials({
  primary: 0xe8a33a,
  secondary: 0xfff4d0,
  accent: 0xe5514f,
  structure: 0xd08a4a,
  lights: 0xfff1b0,
});

/** Fixed-palette attraction bulb materials the night look must drive alongside per-palette channels. */
export const SHARED_LIGHT_CHANNELS: THREE.MeshStandardMaterial[] = [FERRIS_CHANNELS.lights, SPINNING_CHANNELS.lights];

function createCatalogVisual(
  mats: ParkMaterials,
  carouselChannels: ChannelMaterials,
  coasterChannels: ChannelMaterials,
  spec: EntityRenderSpec,
): { object: THREE.Object3D; usesElapsed: boolean; usesDelta: boolean } {
  switch (spec.visual) {
    case 'carousel': {
      const carousel = createCarousel(mats, carouselChannels, { level: spec.level });
      carousel.scale.setScalar(CAROUSEL_SCALE);
      tagVisual(carousel, spec, 'carousel');
      return { object: carousel, usesElapsed: true, usesDelta: false };
    }
    case 'spinningRide': {
      const ride = createSpinningRide(mats, SPINNING_CHANNELS, { level: spec.level });
      ride.scale.setScalar(SPINNING_SCALE);
      tagVisual(ride, spec, 'spinningRide');
      return { object: ride, usesElapsed: true, usesDelta: false };
    }
    case 'pirateShip': {
      const ride = createPirateShip(mats, { level: spec.level });
      ride.scale.setScalar(PIRATE_SCALE);
      ride.rotation.y = PIRATE_YAW;
      tagVisual(ride, spec, 'pirateShip');
      return { object: ride, usesElapsed: true, usesDelta: false };
    }
    case 'ferrisWheel': {
      const ride = createFerrisWheel(mats, FERRIS_CHANNELS, { level: spec.level });
      ride.scale.setScalar(FERRIS_SCALE);
      tagVisual(ride, spec, 'ferrisWheel');
      return { object: ride, usesElapsed: true, usesDelta: false };
    }
    case 'coaster': {
      const coaster = createCoaster(mats, coasterChannels, { level: spec.level });
      coaster.scale.setScalar(COASTER_SCALE);
      tagVisual(coaster, spec, 'coaster');
      return { object: coaster, usesElapsed: false, usesDelta: true };
    }
    case 'snackKiosk': {
      const kiosk = createKiosk(mats);
      kiosk.scale.setScalar(KIOSK_SCALE);
      tagVisual(kiosk, spec, 'snackKiosk');
      return { object: kiosk, usesElapsed: false, usesDelta: false };
    }
    case 'drinkKiosk': {
      const kiosk = createDrinkKiosk(mats);
      kiosk.scale.setScalar(KIOSK_SCALE);
      tagVisual(kiosk, spec, 'drinkKiosk');
      return { object: kiosk, usesElapsed: false, usesDelta: false };
    }
    case 'bench': {
      const bench = createBenches(mats, [{ x: 0, z: 0, rotY: BENCH_PLACEMENT_YAW }], 0);
      tagVisual(bench, spec, 'bench');
      return { object: bench, usesElapsed: false, usesDelta: false };
    }
    case 'tree': {
      const tree = createTrees(mats, [{ x: 0, z: 0, scale: 0.72 }], [], 0);
      tagVisual(tree, spec, 'tree');
      return { object: tree, usesElapsed: false, usesDelta: false };
    }
    case 'lamp': {
      const lamp = createLamps(mats, [{ x: 0, z: 0 }], 0);
      tagVisual(lamp, spec, 'lamp');
      return { object: lamp, usesElapsed: false, usesDelta: false };
    }
    default: {
      const placeholder = createMigrationPlaceholder(mats, spec);
      return { object: placeholder, usesElapsed: false, usesDelta: false };
    }
  }
}

function disposeLayerObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) {
      mesh.geometry.dispose();
    }
  });
}

function entityUpdatersOf(root: THREE.Group): Map<string, ParkUpdater> {
  if (!(root.userData.entityUpdaters instanceof Map)) {
    root.userData.entityUpdaters = new Map<string, ParkUpdater>();
  }
  return root.userData.entityUpdaters as Map<string, ParkUpdater>;
}

function bindParkRootUpdate(root: THREE.Group): void {
  root.userData.update = (elapsed: number, delta: number) => {
    for (const update of (root.userData.staticUpdaters ?? []) as ParkUpdater[]) {
      update(elapsed, delta);
    }
    for (const update of entityUpdatersOf(root).values()) {
      update(elapsed, delta);
    }
  };
}

function entityIdentityKey(spec: EntityRenderSpec): string {
  return `${spec.kind}|${spec.visual}|${spec.level}`;
}

function entityPoseChanged(previous: EntityRenderSpec, next: EntityRenderSpec): boolean {
  return (
    previous.x !== next.x ||
    previous.z !== next.z ||
    previous.rotation !== next.rotation ||
    previous.slotId !== next.slotId
  );
}

function queueLayoutChanged(previous: QueueLane | undefined, next: QueueLane | undefined): boolean {
  if (!previous && !next) {
    return false;
  }
  if (!previous || !next) {
    return true;
  }
  return (
    previous.layout.start.x !== next.layout.start.x ||
    previous.layout.start.y !== next.layout.start.y ||
    previous.layout.end.x !== next.layout.end.x ||
    previous.layout.end.y !== next.layout.end.y ||
    previous.layout.width !== next.layout.width ||
    previous.occupancy !== next.occupancy
  );
}

function queueLanesEquivalent(left: readonly QueueLane[], right: readonly QueueLane[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  const keyOf = (lane: QueueLane): string =>
    `${lane.entityId}|${lane.layout.start.x}|${lane.layout.start.y}|${lane.layout.end.x}|${lane.layout.end.y}|${lane.occupancy}`;
  const leftKeys = left.map(keyOf).sort();
  const rightKeys = right.map(keyOf).sort();
  return leftKeys.every((key, index) => key === rightKeys[index]);
}

function poseCatalogVisual(
  object: THREE.Object3D,
  spec: EntityRenderSpec,
  parkTop: number,
  attractionQueue: QueueLane | undefined,
): void {
  const lift = spec.visual === 'carousel' ? 0.04 : 0;
  object.position.set(spec.x, parkTop + lift, spec.z);
  const baseYaw = typeof object.userData.baseYaw === 'number' ? object.userData.baseYaw : 0;
  object.rotation.y = baseYaw + THREE.MathUtils.degToRad(spec.rotation);
  if (spec.visual === 'lamp' || spec.visual === 'bench') {
    snapPlacedPropToGround(object, parkTop);
  }
  if (spec.visual === 'carousel' && attractionQueue) {
    object.rotation.y = -Math.atan2(
      attractionQueue.layout.end.y - spec.z,
      attractionQueue.layout.end.x - spec.x,
    );
  }
  orientGlowLayers(object, object.rotation.y);
}

function createQueueVisual(mats: ParkMaterials, lane: QueueLane, parkTop: number): THREE.Object3D {
  const queueVisual = createRideQueue(mats, lane.layout, parkTop + 0.06, queueSignLabel(lane.kind));
  queueVisual.userData.migration = {
    visual: 'queue',
    kind: lane.kind,
    slotId: lane.slotId,
    entityId: lane.entityId,
  };
  return queueVisual;
}

function mountEntityBundle(
  root: THREE.Group,
  mats: ParkMaterials,
  carouselChannels: ChannelMaterials,
  coasterChannels: ChannelMaterials,
  spec: EntityRenderSpec,
  parkTop: number,
  queueLanes: readonly QueueLane[],
): THREE.Group {
  const created = createCatalogVisual(mats, carouselChannels, coasterChannels, spec);
  created.object.userData.baseYaw = created.object.rotation.y;
  const bundle = new THREE.Group();
  bundle.userData.parkLayer = 'entity';
  bundle.userData.entityId = spec.id;
  bundle.add(created.object);
  bundle.userData.catalogVisual = created.object;
  const attractionQueue = queueLanes.find((lane) => lane.entityId === spec.id);
  poseCatalogVisual(created.object, spec, parkTop, attractionQueue);
  if (spec.visual !== 'placeholder' && spec.visual !== 'bench' && spec.visual !== 'tree' && spec.visual !== 'lamp') {
    const levelMarker = createCarouselLevelMarker(mats, spec.level);
    levelMarker.userData.migration = {
      visual: 'levelMarker',
      entityId: spec.id,
      kind: spec.kind,
      slotId: spec.slotId,
      level: spec.level,
    };
    levelMarker.position.set(spec.x, parkTop, spec.z);
    bundle.add(levelMarker);
    bundle.userData.levelMarker = levelMarker;
  }
  if (attractionQueue) {
    const queueVisual = createQueueVisual(mats, attractionQueue, parkTop);
    bundle.add(queueVisual);
    bundle.userData.queueVisual = queueVisual;
  }
  if (created.usesElapsed || created.usesDelta) {
    entityUpdatersOf(root).set(spec.id, (elapsed, delta) => {
      if (created.usesDelta) {
        created.object.userData.update(delta);
      } else {
        created.object.userData.update(elapsed);
      }
    });
  }
  return bundle;
}

function poseEntityBundle(
  bundle: THREE.Group,
  spec: EntityRenderSpec,
  mats: ParkMaterials,
  parkTop: number,
  attractionQueue: QueueLane | undefined,
): void {
  const catalog = bundle.userData.catalogVisual as THREE.Object3D;
  poseCatalogVisual(catalog, spec, parkTop, attractionQueue);
  const marker = bundle.userData.levelMarker as THREE.Object3D | undefined;
  if (marker) {
    marker.position.set(spec.x, parkTop, spec.z);
  }
  const previousQueue = bundle.userData.queueVisual as THREE.Object3D | undefined;
  if (attractionQueue) {
    if (previousQueue) {
      bundle.remove(previousQueue);
      disposeLayerObject(previousQueue);
    }
    const queueVisual = createQueueVisual(mats, attractionQueue, parkTop);
    bundle.add(queueVisual);
    bundle.userData.queueVisual = queueVisual;
  } else if (previousQueue) {
    bundle.remove(previousQueue);
    disposeLayerObject(previousQueue);
    bundle.userData.queueVisual = null;
  }
}

function entityBundlesById(layer: THREE.Group): Map<string, THREE.Group> {
  const bundles = new Map<string, THREE.Group>();
  for (const child of layer.children) {
    const entityId = child.userData.entityId as string | undefined;
    if (entityId) {
      bundles.set(entityId, child as THREE.Group);
    }
  }
  return bundles;
}

export function collectParkEntityVisuals(root: THREE.Group): THREE.Object3D[] {
  const layer = root.userData.entitiesLayer as THREE.Group | undefined;
  if (!layer) {
    return [];
  }
  return layer.children.flatMap((bundle) => {
    const catalog = bundle.userData.catalogVisual as THREE.Object3D | undefined;
    return catalog ? [catalog] : [];
  });
}

function ensureEntitiesLayer(root: THREE.Group): THREE.Group {
  let layer = root.userData.entitiesLayer as THREE.Group | undefined;
  if (layer) {
    return layer;
  }
  layer = new THREE.Group();
  layer.userData.parkLayer = 'entities';
  root.add(layer);
  root.userData.entitiesLayer = layer;
  return layer;
}

function buildQueuePathLayer(
  mats: ParkMaterials,
  model: MigrationRenderModel,
  parkTop: number,
): THREE.Group | null {
  const pieces = queueLanesFromModel(model).map((lane) => queueLanePiece(lane.layout));
  if (pieces.length === 0) {
    return null;
  }
  const network = createPathNetwork(mats, pieces, parkTop);
  network.userData.parkLayer = 'queuePath';
  return network;
}

function kioskStandingFromModel(model: MigrationRenderModel): Array<{ x: number; z: number; facing: number }> {
  return model.entities.filter((entity) => kioskHasQueue(entity.kind)).map((entity) => {
    const atWindow = rotatePointAroundY(entity.x, entity.z + 0.72, entity.x, entity.z, entity.rotation);
    return {
      x: atWindow.x,
      z: atWindow.z,
      facing: THREE.MathUtils.degToRad(entity.rotation) + Math.PI,
    };
  });
}

function replaceParkLayer(root: THREE.Group, key: string, next: THREE.Object3D | null): void {
  const previous = root.userData[key] as THREE.Object3D | undefined;
  if (previous) {
    root.remove(previous);
    disposeLayerObject(previous);
  }
  root.userData[key] = next ?? null;
  if (next) {
    root.add(next);
  }
}

function lawnCentersFromModel(model: MigrationRenderModel): Array<[number, number]> {
  const pavedKeys = new Set(model.pathFloorCells.map((cell) => `${cell.x.toFixed(3)},${cell.z.toFixed(3)}`));
  return unlockedCanvasSubcellCenters(model.parity.unlockedLandIds)
    .map((point) => [point.x, point.z] as [number, number])
    .filter(([x, z]) => !pavedKeys.has(`${x.toFixed(3)},${z.toFixed(3)}`));
}

function buildLawnLayer(mats: ParkMaterials, model: MigrationRenderModel, parkTop: number): THREE.Group | null {
  const lawnCenters = lawnCentersFromModel(model);
  if (lawnCenters.length === 0) {
    return null;
  }
  const lawn = createExactCanvasLawn(mats, lawnCenters, parkTop);
  lawn.userData.parkLayer = 'lawn';
  return lawn;
}

function buildFloorLayer(mats: ParkMaterials, model: MigrationRenderModel, parkTop: number): THREE.Group | null {
  if (model.pathFloorCells.length === 0) {
    return null;
  }
  const floors = createPathFloorPresentation(
    mats,
    model.pathFloorCells.map((cell) => ({
      x: cell.x,
      z: cell.z,
      style: cell.style,
      lift: pathFloorCellLift(cell.coversLawn),
    })),
    parkTop,
  );
  floors.userData.parkLayer = 'floors';
  return floors;
}

function buildLandOverlayLayer(model: MigrationRenderModel, parkTop: number): THREE.Group | null {
  const showLandOverlays = Boolean(model.landMode);
  if (!showLandOverlays) {
    return null;
  }
  const group = new THREE.Group();
  group.userData.parkLayer = 'land';
  const overlayLands = model.lands.filter((land) => land.parcelId.startsWith('plot_') && land.previewKind);
  for (const land of overlayLands) {
    if (land.previewKind === 'owned') {
      group.add(createOwnedIslandPlotPreview(land, parkTop));
      continue;
    }
    if (
      land.previewKind === 'distant' ||
      land.previewKind === 'frontier' ||
      land.previewKind === 'recommended'
    ) {
      group.add(createDistantIslandPlotPreview(land, parkTop, land.previewKind));
    }
  }
  return group.children.length > 0 ? group : null;
}

function buildSelectionLayer(model: MigrationRenderModel, parkTop: number): THREE.Mesh | null {
  const selected = resolveSelectionAnchor(model);
  if (!selected) {
    return null;
  }
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(CELL_SIZE * 0.42, CELL_SIZE * 0.48, 28),
    new THREE.MeshBasicMaterial({
      color: 0xff7a3a,
      transparent: true,
      opacity: 0.92,
      side: THREE.DoubleSide,
      depthWrite: false,
    }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.set(selected.x, parkTop + 0.16, selected.z);
  ring.userData.migration = { visual: 'selection', slotId: selected.slotId };
  ring.userData.parkLayer = 'selection';
  return ring;
}

function queueSlotsFromRoot(root: THREE.Group): ReturnType<typeof restSlotsForLane> {
  const queueLanes = (root.userData.queueLanes ?? []) as ReturnType<typeof queueLanesFromModel>;
  return queueLanes.flatMap((lane) => restSlotsForLane(lane).map((slot) => ({ ...slot })));
}

function asPathPoints(route: readonly WorldPoint[]): PathPoint[] {
  return route.map((point) => ({ x: point.x, z: point.z }));
}

export interface RemappedPatrolWalker extends PatrolWalkerSpec {
  routeIndex: number;
}

function walkerRouteIndex(walker: PatrolWalkerSpec, routeCount: number, index: number): number {
  if (routeCount <= 0) {
    return 0;
  }
  const remapped = walker as RemappedPatrolWalker;
  if (Number.isInteger(remapped.routeIndex)) {
    return ((remapped.routeIndex % routeCount) + routeCount) % routeCount;
  }
  return index % routeCount;
}

function nearestRouteIndex(routes: readonly WorldPoint[][], x: number, z: number): number {
  let best = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let i = 0; i < routes.length; i += 1) {
    const points = asPathPoints(routes[i]!);
    const along = nearestDistanceOnClosedPath(points, x, z);
    const pose = poseOnClosedPath(points, along);
    const distance = Math.hypot(pose.x - x, pose.z - z);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = i;
    }
  }
  return best;
}

export function remappedProductionWalkers(
  previousRoutes: readonly WorldPoint[][],
  nextRoutes: readonly WorldPoint[][],
  elapsed: number,
  currentWalkers: readonly PatrolWalkerSpec[] = PRODUCTION_PATROL_WALKERS,
): RemappedPatrolWalker[] {
  if (nextRoutes.length === 0) {
    return currentWalkers.map((walker, index) => ({ ...walker, routeIndex: index }));
  }
  if (previousRoutes.length === 0) {
    return currentWalkers.map((walker, index) => ({
      ...walker,
      routeIndex: index % nextRoutes.length,
    }));
  }
  return currentWalkers.map((walker, index) => {
    const previousIndex = walkerRouteIndex(walker, previousRoutes.length, index);
    const previous = asPathPoints(previousRoutes[previousIndex]!);
    const matching = indexOfEquivalentVisitorRoute(previous, nextRoutes.map(asPathPoints));
    if (matching >= 0) {
      return { ...walker, routeIndex: matching };
    }
    const pose = posePatrolWalker(previous, walker, elapsed);
    const routeIndex = nearestRouteIndex(nextRoutes, pose.x, pose.z);
    const walked = walkedTime(elapsed, walker.pauseEvery, walker.pauseFor, walker.pausePhase);
    return {
      ...walker,
      routeIndex,
      offset: remapWalkerOffset({
        previous: { x: pose.x, z: pose.z },
        nextPoints: asPathPoints(nextRoutes[routeIndex]!),
        walked,
        speed: walker.speed,
      }),
    };
  });
}

function patrolRoutesForWalkers(model: MigrationRenderModel, walkers: readonly PatrolWalkerSpec[]): Route[] {
  const visitorPolylines = model.visitorRoutes.length > 0 ? model.visitorRoutes : [];
  if (visitorPolylines.length === 0) {
    return [];
  }
  return walkers.map((walker, index) => {
    const polyline = visitorPolylines[walkerRouteIndex(walker, visitorPolylines.length, index)]!;
    return {
      points: polyline.map((point) => v2(point.x, point.z)),
      speed: walker.speed,
      offset: walker.offset,
      lane: walker.lane,
      pauseEvery: walker.pauseEvery,
      pauseFor: walker.pauseFor,
      pausePhase: walker.pausePhase,
    };
  });
}

function buildVisitorLayer(
  root: THREE.Group,
  mats: ParkMaterials,
  model: MigrationRenderModel,
  parkTop: number,
  walkers: readonly PatrolWalkerSpec[] = PRODUCTION_PATROL_WALKERS,
): THREE.Group {
  const animatedSlots = queueSlotsFromRoot(root);
  root.userData.animatedSlots = animatedSlots;
  const kioskStanding = kioskStandingFromModel(model);
  root.userData.kioskStanding = kioskStanding;
  const visitors = createVisitors(mats, parkTop + 0.06, {
    routes: patrolRoutesForWalkers(model, walkers),
    standing: kioskStanding,
    queue: animatedSlots,
  });
  visitors.userData.migration = { visual: 'pathPatrol' };
  visitors.userData.parkLayer = 'visitors';
  visitors.userData.patrolWalkers = walkers;
  return visitors;
}

export function countParkLayers(
  root: THREE.Group,
  layer: 'lawn' | 'floors' | 'land' | 'selection' | 'visitors' | 'entity' | 'entities',
): number {
  let count = 0;
  root.traverse((object) => {
    if (object.userData.parkLayer === layer) {
      count += 1;
    }
  });
  return count;
}

function syncParkVisitorLayer(root: THREE.Group, model: MigrationRenderModel): void {
  const mats = root.userData.parkMats as ParkMaterials;
  const parkTop = root.userData.parkTop as number;
  const previous = root.userData.migrationModel as MigrationRenderModel | undefined;
  const previousRoutes = previous?.visitorRoutes ?? [];
  const nextRoutes = model.visitorRoutes;
  const live = root.userData.visitorsLayer as THREE.Group | undefined;
  const nextStanding = kioskStandingFromModel(model);
  const liveStanding = (root.userData.kioskStanding ?? []) as Array<{ x: number; z: number; facing: number }>;
  const nextSlotCount = ((root.userData.queueLanes ?? []) as QueueLane[]).reduce(
    (sum, lane) => sum + lane.occupancy,
    0,
  );
  const liveSlots = (root.userData.animatedSlots ?? []) as Array<Record<string, unknown>>;
  const elapsed = typeof root.userData.visitorElapsed === 'number' ? root.userData.visitorElapsed : 0;
  const countsCompatible =
    Boolean(live) && liveStanding.length === nextStanding.length && liveSlots.length === nextSlotCount;

  if (countsCompatible) {
    for (let i = 0; i < liveStanding.length; i += 1) {
      Object.assign(liveStanding[i]!, nextStanding[i]!);
    }
    const refreshed = queueSlotsFromRoot(root);
    for (let i = 0; i < liveSlots.length; i += 1) {
      Object.assign(liveSlots[i]!, refreshed[i]!);
    }
    if (!visitorRouteSetsEquivalent(previousRoutes, nextRoutes)) {
      const currentWalkers =
        (live?.userData.patrolWalkers as PatrolWalkerSpec[] | undefined) ?? PRODUCTION_PATROL_WALKERS;
      const walkers = remappedProductionWalkers(previousRoutes, nextRoutes, elapsed, currentWalkers);
      const routes = patrolRoutesForWalkers(model, walkers);
      if (typeof live?.userData.retargetPatrolRoutes === 'function' && live.userData.retargetPatrolRoutes(routes)) {
        live.userData.patrolWalkers = walkers;
        live.userData.update?.(elapsed);
        return;
      }
    } else {
      live?.userData.update?.(elapsed);
      return;
    }
  }

  const currentWalkers =
    (live?.userData.patrolWalkers as PatrolWalkerSpec[] | undefined) ?? PRODUCTION_PATROL_WALKERS;
  const walkers = remappedProductionWalkers(previousRoutes, nextRoutes, elapsed, currentWalkers);
  replaceParkLayer(root, 'visitorsLayer', buildVisitorLayer(root, mats, model, parkTop, walkers));
  const next = root.userData.visitorsLayer as THREE.Group | undefined;
  next?.userData.update?.(elapsed);
}

export function syncParkFloorLayers(root: THREE.Group, model: MigrationRenderModel): void {
  const mats = root.userData.parkMats as ParkMaterials;
  const parkTop = root.userData.parkTop as number;
  replaceParkLayer(root, 'lawnLayer', buildLawnLayer(mats, model, parkTop));
  replaceParkLayer(root, 'floorLayer', buildFloorLayer(mats, model, parkTop));
  syncParkVisitorLayer(root, model);
  root.userData.migrationModel = model;
}

export function syncParkSelectionLayer(root: THREE.Group, model: MigrationRenderModel): void {
  const parkTop = root.userData.parkTop as number;
  replaceParkLayer(root, 'selectionLayer', buildSelectionLayer(model, parkTop));
  root.userData.migrationModel = model;
}

export function syncParkLandOverlayLayer(root: THREE.Group, model: MigrationRenderModel): void {
  const parkTop = root.userData.parkTop as number;
  replaceParkLayer(root, 'landOverlayLayer', buildLandOverlayLayer(model, parkTop));
  syncParkSelectionLayer(root, model);
}

export function createParkFromState(
  mats: ParkMaterials,
  carouselChannels: ChannelMaterials,
  coasterChannels: ChannelMaterials,
  model: MigrationRenderModel,
): { root: THREE.Group; parkTop: number } {
  const root = new THREE.Group();
  const size = islandSize(model);
  const island = createIsland(mats, size.width, size.depth);
  root.add(island.group);
  const parkTop = island.parkTop;
  root.userData.parkMats = mats;
  root.userData.parkTop = parkTop;

  const queueLanes = queueLanesFromModel(model);
  root.userData.queueLanes = queueLanes;
  root.userData.carouselChannels = carouselChannels;
  root.userData.coasterChannels = coasterChannels;
  replaceParkLayer(root, 'queuePathLayer', buildQueuePathLayer(mats, model, parkTop));

  const unlockedSlots = model.slots;
  const cueMode: SubgridCueMode = model.buildMode ? 'build' : 'normal';
  const canvasCenters = unlockedCanvasSubcellCenters(model.parity.unlockedLandIds).map(
    (point) => [point.x, point.z] as [number, number],
  );
  const lawn = buildLawnLayer(mats, model, parkTop);
  replaceParkLayer(root, 'lawnLayer', lawn);
  if (canvasCenters.length > 0 || unlockedSlots.length > 0) {
    const canvasGrid = createExactSubcellGrid(mats, canvasCenters, parkTop, cueMode);
    root.add(canvasGrid);
    root.userData.setSubgridCueMode = (mode: SubgridCueMode) => {
      setSubgridCueMode(canvasGrid, mode);
    };
  }
  replaceParkLayer(root, 'floorLayer', buildFloorLayer(mats, model, parkTop));
  replaceParkLayer(root, 'landOverlayLayer', buildLandOverlayLayer(model, parkTop));

  const entitiesLayer = ensureEntitiesLayer(root);
  for (const spec of model.entities) {
    entitiesLayer.add(mountEntityBundle(root, mats, carouselChannels, coasterChannels, spec, parkTop, queueLanes));
  }

  const entrance = createEntrance(mats);
  entrance.position.set(model.entrance.x, parkTop, model.entrance.z + 0.15);
  root.add(entrance);

  const booth = createTicketBooth(mats);
  booth.position.set(model.entrance.x + 2.85, parkTop, model.entrance.z - 0.55);
  booth.rotation.y = -0.18;
  root.add(booth);

  const halfW = size.width / 2 - 0.55;
  const halfD = size.depth / 2 - 0.45;
  root.add(
    createPicketFence(
      mats,
      [
        [v2(-halfW + 0.4, halfD - 0.35), v2(-2.4, halfD - 0.35)],
        [v2(2.4, halfD - 0.35), v2(halfW - 0.4, halfD - 0.35)],
        [v2(-halfW, halfD - 0.8), v2(-halfW, 1.1)],
        [v2(halfW, halfD - 0.8), v2(halfW, 1.4)],
      ],
      parkTop,
    ),
  );
  root.add(
    createHedges(
      mats,
      [
        { x: halfW, z: 0.1, rotY: Math.PI / 2 },
        { x: halfW, z: -1.2, rotY: Math.PI / 2 },
        { x: -halfW, z: 0.0, rotY: Math.PI / 2 },
        { x: -halfW, z: -1.3, rotY: Math.PI / 2 },
      ],
      parkTop,
    ),
  );
  root.add(
    createTrees(
      mats,
      [
        { x: -halfW + 0.2, z: -halfD + 0.35, scale: 1.0 },
        { x: -halfW + 0.25, z: halfD - 0.2, scale: 0.9 },
        { x: halfW - 0.2, z: -halfD + 0.35, scale: 0.95 },
        { x: halfW - 0.2, z: halfD - 0.2, scale: 1.0 },
        { x: -4.4, z: halfD - 0.18, scale: 0.8 },
        { x: 4.4, z: halfD - 0.18, scale: 0.82 },
      ],
      [{ x: -halfW + 0.45, z: -halfD + 0.9, scale: 0.95 }],
      parkTop,
    ),
  );
  root.add(
    createShrubs(
      mats,
      [
        { x: -4.6, z: halfD - 0.22, scale: 1.0 },
        { x: 4.6, z: halfD - 0.22, scale: 1.0 },
        { x: halfW - 0.18, z: 0.4, scale: 0.85 },
      ],
      parkTop,
    ),
  );

  // Default loop remains model.waypoints; live patrols use the walkable-graph polylines.
  replaceParkLayer(root, 'visitorsLayer', buildVisitorLayer(root, mats, model, parkTop));
  root.userData.staticUpdaters = [
    (elapsed: number) => entrance.userData.update(elapsed),
    (elapsed: number, delta: number) => {
      const lanes = (root.userData.queueLanes ?? []) as QueueLane[];
      const slots = (root.userData.animatedSlots ?? []) as ReturnType<typeof restSlotsForLane>;
      let offset = 0;
      for (const lane of lanes) {
        const poses = queueGuestPoses(lane, elapsed);
        applyQueuePoses(slots.slice(offset, offset + lane.occupancy), poses);
        offset += lane.occupancy;
      }
      root.userData.visitorElapsed = elapsed;
      const liveVisitors = root.userData.visitorsLayer as THREE.Group | undefined;
      liveVisitors?.userData.update?.(elapsed, delta);
    },
  ] satisfies ParkUpdater[];

  replaceParkLayer(root, 'selectionLayer', buildSelectionLayer(model, parkTop));

  bindParkRootUpdate(root);
  root.userData.visitorElapsed = 0;
  root.userData.update(0, 0);
  root.userData.migrationModel = model;
  root.userData.queueSnapshot = (elapsed: number): QueueEvidence =>
    queueEvidenceFromLanes((root.userData.queueLanes ?? []) as QueueLane[], elapsed);
  root.userData.patrolSnapshot = (elapsed: number) => productionPatrolPoses(model.waypoints, elapsed);
  return { root, parkTop };
}

export function syncParkEntityLayer(root: THREE.Group, model: MigrationRenderModel): void {
  const mats = root.userData.parkMats as ParkMaterials;
  const parkTop = root.userData.parkTop as number;
  const carouselChannels = root.userData.carouselChannels as ChannelMaterials;
  const coasterChannels = root.userData.coasterChannels as ChannelMaterials;
  const previous = root.userData.migrationModel as MigrationRenderModel | undefined;
  const previousLanes = (root.userData.queueLanes ?? []) as QueueLane[];
  const queueLanes = queueLanesFromModel(model);
  root.userData.queueLanes = queueLanes;

  const layer = ensureEntitiesLayer(root);
  const previousById = new Map((previous?.entities ?? []).map((entity) => [entity.id, entity]));
  const nextIds = new Set(model.entities.map((entity) => entity.id));
  const live = entityBundlesById(layer);

  for (const [entityId, bundle] of live) {
    if (!nextIds.has(entityId)) {
      layer.remove(bundle);
      disposeLayerObject(bundle);
      entityUpdatersOf(root).delete(entityId);
    }
  }

  for (const spec of model.entities) {
    const previousSpec = previousById.get(spec.id);
    const bundle = live.get(spec.id);
    const attractionQueue = queueLanes.find((lane) => lane.entityId === spec.id);
    const previousQueue = previousLanes.find((lane) => lane.entityId === spec.id);
    if (bundle && previousSpec && entityIdentityKey(previousSpec) === entityIdentityKey(spec)) {
      if (entityPoseChanged(previousSpec, spec) || queueLayoutChanged(previousQueue, attractionQueue)) {
        poseEntityBundle(bundle, spec, mats, parkTop, attractionQueue);
      }
      continue;
    }
    if (bundle) {
      layer.remove(bundle);
      disposeLayerObject(bundle);
      entityUpdatersOf(root).delete(spec.id);
    }
    layer.add(mountEntityBundle(root, mats, carouselChannels, coasterChannels, spec, parkTop, queueLanes));
  }

  if (!queueLanesEquivalent(previousLanes, queueLanes)) {
    replaceParkLayer(root, 'queuePathLayer', buildQueuePathLayer(mats, model, parkTop));
  }

  syncParkVisitorLayer(root, model);
  syncParkSelectionLayer(root, model);
  root.userData.migrationModel = model;
}
